/** Een beurt in de chat: wat er meteen in beeld komt en hoe het verloopt. Fix 1.
 *
 *  **FRANKS BEVINDING VAN 11 OKTOBER 2026:** bij een nieuwe chat was 10 tot 15
 *  seconden niets te zien. Gemeten waren dat twee dingen. Het bericht kwam pas
 *  in beeld NA de preflight en het aanmaken van het gesprek, en het aanmaken
 *  liet `fetchMessages` de lijst overschrijven met de nog lege lijst van de
 *  server. Daarmee verdween ook de antwoordbel, en kwam alles pas aan het eind
 *  in een keer.
 *
 *  Puur, zodat het op uitkomst te toetsen is: `ChatPage` roept dit aan en
 *  bewaart de uitkomst.
 */

/** Wat er onder de bezig-aanduiding staat voordat de dienst een fase meldt. */
export const EERSTE_FASE = "versturen";

/** "bronnen lezen" wordt "Bronnen lezen…". De dienst levert de woorden. */
export function faseTekst(fase) {
  const woorden = String(fase || EERSTE_FASE).trim() || EERSTE_FASE;
  return `${woorden.charAt(0).toUpperCase()}${woorden.slice(1)}…`;
}

/** Zet de vraag en een lege antwoordbel met bezig-aanduiding in beeld. */
export function beginBeurt(berichten, { inhoud, bijlagen = [], gesprekId = "", nu = Date.now() }) {
  const gebruikerId = `temp-${nu}`;
  const assistentId = `temp-assistant-${nu}`;
  const tijd = new Date(nu).toISOString();
  return {
    gebruikerId,
    assistentId,
    berichten: [
      ...berichten,
      { id: gebruikerId, conversation_id: gesprekId, role: "user", content: inhoud,
        attachments: bijlagen, timestamp: tijd },
      { id: assistentId, conversation_id: gesprekId, role: "assistant", content: "",
        timestamp: tijd, bezig: true, fase: EERSTE_FASE },
    ],
  };
}

/** De fase waarin het model nadenkt voordat het schrijft (backend `fasen.DENKT`). */
export const DENKT = "nadenken";

/**
 * Een fase van de dienst op de antwoordbel. Raakt geen ander bericht.
 *
 * PUNT 4a, 12 oktober 2026: elke fase krijgt het moment waarop hij begon
 * (`faseSinds`), zodat het nadenken met een teller op het scherm staat. Gaat de
 * bel van nadenken naar iets anders, dan blijft de duur staan als `denktijd`
 * (hele seconden), en die staat daarna onder het antwoord.
 */
export function pasFaseToe(berichten, assistentId, fase, nu = Date.now()) {
  return berichten.map((m) => {
    if (m.id !== assistentId) return m;
    const uit = { ...m, fase, faseSinds: nu };
    if (m.fase === DENKT && fase !== DENKT && m.faseSinds) {
      uit.denktijd = (m.denktijd || 0) + Math.round((nu - m.faseSinds) / 1000);
    }
    return uit;
  });
}

/** "Nadenken… 12 s" terwijl het duurt. PUUR. */
export function denkTeller(faseSinds, nu = Date.now()) {
  return `${Math.max(0, Math.floor((nu - faseSinds) / 1000))} s`;
}

/** De beurt ging niet door (escalatievraag, geen gesprek): haal hem weg. */
export function haalBeurtWeg(berichten, ids) {
  const weg = new Set(ids);
  return berichten.filter((m) => !weg.has(m.id));
}

/**
 * B2, 11 oktober 2026: wat er live op het scherm stond, wordt het nagekeken
 * antwoord. Na de controles kan een wachter de tekst hebben aangepast; dan komt
 * de nieuwe tekst ervoor in de plaats, met de melding van de dienst erbij. Een
 * lege melding (bij Stop of een fout) zegt niets extra's.
 */
/**
 * GL214-8 (besluit Frank 28 oktober 2026): raakte een controle alleen de TAAL van het antwoord,
 * dan stuurt de dienst een lichte regel in plaats van de volle melding. Letterlijk gelijk aan
 * `backend/services/live.py` TAAL_GEZET.
 */
export const TAALREGELS = ["In het Nederlands gezet", "In het Engels gezet"];

export function isTaalregel(melding) {
  return TAALREGELS.includes(melding);
}

export function vervangAntwoord(berichten, id, tekst, melding) {
  return berichten.map((m) => (m.id === id
    ? { ...m, content: tekst, vervangen: melding || "" }
    : m));
}

/**
 * Wat alleen in de browser bestaat, over een herlading van de lijst heen. PUUR.
 *
 * Na elke beurt haalt ChatPage het gesprek opnieuw van de server, en die kent de
 * denktijd niet (punt 4a, 12 oktober 2026: op de kopie stond "Nagedacht 3 s"
 * daardoor nooit onder het antwoord). Per bericht-id gaat hij mee.
 */
// A3 punt 2, 15 oktober 2026: ook de melding "Na de controles is dit antwoord
// aangepast" (`vervangen`) bestaat alleen hier, en verdween om dezelfde reden.
const ALLEEN_IN_DE_BROWSER = ["denktijd", "vervangen"];

export function behoudDenktijd(vanServer, eerder) {
  const bewaard = new Map();
  for (const m of eerder || []) {
    const velden = {};
    for (const veld of ALLEEN_IN_DE_BROWSER) if (m[veld]) velden[veld] = m[veld];
    if (Object.keys(velden).length) bewaard.set(m.id, velden);
  }
  return vanServer.map((m) => (bewaard.has(m.id) ? { ...m, ...bewaard.get(m.id) } : m));
}
